import { Routes, Route } from 'react-router-dom';
// `/toast` has no flat `dist/toast.d.ts` — the real export is nested at
// `dist/toast/index.d.ts`, so the package's `./* -> ./dist/*.d.ts` wildcard
// only resolves via the explicit `/index` suffix (verified against the
// installed `@clay-run/glaze` package.json `exports` map).
import { GlobalGlazeToastRegion } from '@clay-run/glaze/toast/index';
import { useCurrentUser } from '../_shared/hooks';
import ScansPage from './components/ScansPage';
import ViewSwitcher from './components/ViewSwitcher';
import EnrichmentManager from './components/EnrichmentManager';

/**
 * App root (U1/U3/U4). `ViewSwitcher` owns Home / Scan-review / Settings
 * routing and the 960px master-detail responsive shell (R7) —
 * `useLeadScannerStore`'s `view` field (Key Technical Decision 13).
 *
 * `<Routes>` added for the unified Scans page (plan
 * plans/2026-08-26-001-lead-scanner-scans-page-plan.md) — its own
 * URL-shareable filters/open-scan state need a real router route, not
 * another `useLeadScannerStore.view` value. Same structure
 * `mini-apps/command-center/index.tsx` already uses: the index route keeps
 * the existing `ViewSwitcher` behavior unchanged, `path="scans"` is the only
 * new route.
 *
 * `EnrichmentManager` (renders nothing) is mounted here — outside/alongside
 * the routed content — so it survives every in-app navigation (Home ↔
 * ScansPage ↔ any open scan): it needs to outlive the `ScanReview` mount
 * that triggered an enrichment run, since the run itself (and the PATCH that
 * applies its result) must keep going even after the rep navigates away to
 * scan another lead. The VISUAL indicator, though, lives inline in
 * `ScanReview.tsx` (right above its "Existing campaign member" search) —
 * mounting it here instead used to float it above the app's own header/nav,
 * easy to scroll past or miss entirely.
 */
export default function LeadScannerApp() {
  const { isLoading } = useCurrentUser();

  if (isLoading) {
    return <div className="p-6 text-content-tertiary">Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Mounted once at the app root — Glaze's imperative toast API
          (`addToast`/`addErrorToast`, used by the mapper's Save/Discard
          flows and Home's intake routing) has no visible output until this
          is rendered somewhere. */}
      <GlobalGlazeToastRegion />
      <EnrichmentManager />
      <Routes>
        <Route index element={<ViewSwitcher />} />
        <Route path="scans" element={<ScansPage />} />
      </Routes>
    </div>
  );
}
